import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Download, Clock, Share } from "lucide-react";
import { pdf } from "@react-pdf/renderer";
import {
  editAssignment,
  exportAssignmentToGoogleClassroom,
  extendQuizAssignment,
  freezeAssignment,
  getGetAssignmentFromTeacherQueryKey,
  getGetAssignmentStudentsQueryKey,
  startQuizAssignment,
  unfreezeAssignment,
  useAssignAssignmentToStudentsNew,
  useEditAssignment,
} from "@packages/api/madlenAPI";
import PDFRenderer from "./pdf-page/PDFRenderer";
import { PDFData } from "./pdf-page/types";
import { ClassroomAndStudentSelector } from "./ClassroomAndStudentSelector";
import {
  BaseAssignmentResponseDto,
  BaseAssignmentResponseDtoStatus,
  BaseQuestionDtoType,
  EditAssignmentRequestBody,
  EditAssignmentRequestBodyNewlyCreatedItem,
  FillTheBlanksQuestionCreateRequestDto,
  MatchQuestionCreateRequestDto,
  OpenEndedQuestionCreateRequestDto,
  TestQuestionCreateRequestDto,
  TrueFalseQuestionCreateRequestDto,
} from "@packages/api/madlenAPI.schemas";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@packages/contexts/ToastContext";
import { Button } from "../../../../core";
import QuestionAddModal from "@features/assignments/components/QuestionAddModal";
import { useNavigate } from "react-router-dom";
import { useMobile } from "@packages/hooks/useMobile";
const DownloadButton = ({
  icon,
  text,
  onClick,
  loading,
  variant = "secondary",
  disabled,
}: {
  icon: React.ReactNode;
  text: string;
  onClick: () => void;
  loading?: boolean;
  variant?: "primary" | "secondary";
  disabled?: boolean;
}) => (
  <Button
    variant="ghost"
    size="sm"
    rounded="lg"
    className={`group active:scale-[0.98] ${
      !loading &&
      (variant === "primary"
        ? "border border-green-800/20"
        : "border border-blue-800/20")
    }`}
    bgColor={
      loading
        ? "bg-gray-100 dark:bg-gray-700"
        : variant === "primary"
          ? "bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/40"
          : "bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/40"
    }
    textColor={
      loading
        ? "text-gray-500 dark:text-gray-400"
        : variant === "primary"
          ? "text-green-800 dark:text-green-100 hover:text-green-900 dark:hover:text-green-200"
          : "text-blue-800 dark:text-blue-100 hover:text-blue-900 dark:hover:text-blue-200"
    }
    disabled={loading || disabled}
    onClick={onClick}
    leftIcon={
      <span className="transition-transform duration-200 group-hover:scale-110">
        {icon}
      </span>
    }
  >
    {text}
  </Button>
);

const QuizTimer = ({
  endDate,
  isFrozen,
}: {
  endDate?: string;
  isFrozen?: boolean;
}) => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState<{
    hours: number;
    minutes: number;
    seconds: number;
  }>({ hours: 0, minutes: 0, seconds: 0 });
  const [isExpiring, setIsExpiring] = useState(false);

  useEffect(() => {
    if (!endDate) return;

    // Calculate the time left (needs to happen whether frozen or not)
    const calculateTimeLeft = () => {
      const now = new Date();
      const end = new Date(endDate);
      const difference = end.getTime() - now.getTime();

      if (difference <= 0) {
        if (!isFrozen) {
          window.location.reload(); // Refresh page when timer ends (only if not frozen)
        }
        return { hours: 0, minutes: 0, seconds: 0 };
      }

      // Check if less than 5 minutes remaining
      if (difference <= 5 * 60 * 1000) {
        setIsExpiring(true);
      } else {
        setIsExpiring(false);
      }

      const hours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      return { hours, minutes, seconds };
    };

    // Calculate initial time left regardless of frozen state
    setTimeLeft(calculateTimeLeft());

    // Only set up the interval if not frozen
    let timer: NodeJS.Timeout | undefined;

    if (!isFrozen) {
      timer = setInterval(() => {
        setTimeLeft(calculateTimeLeft());
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [endDate, isFrozen]);

  const formatTime = (value: number) => {
    return value < 10 ? `0${value}` : value;
  };

  const frozenTextColor = isFrozen
    ? "text-amber-600 dark:text-amber-400"
    : isExpiring
      ? "text-red-600 dark:text-red-400"
      : "text-blue-600 dark:text-blue-300";

  return (
    <div
      className={`flex gap-3 items-center transition-all duration-300 ${isExpiring ? "scale-105" : ""} ${isFrozen ? "opacity-90" : ""}`}
    >
      <div
        className={`flex flex-col items-center ${isExpiring && !isFrozen ? "animate-pulse" : ""}`}
      >
        <div className={`text-4xl font-bold ${frozenTextColor}`}>
          {formatTime(timeLeft.hours)}
        </div>
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {t("common.time.hours")}
        </div>
      </div>

      <div className="text-3xl font-bold text-gray-500 dark:text-gray-400">
        :
      </div>

      <div
        className={`flex flex-col items-center ${isExpiring && !isFrozen ? "animate-pulse" : ""}`}
      >
        <div className={`text-4xl font-bold ${frozenTextColor}`}>
          {formatTime(timeLeft.minutes)}
        </div>
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {t("common.time.minutes")}
        </div>
      </div>

      <div className="text-3xl font-bold text-gray-500 dark:text-gray-400">
        :
      </div>

      <div
        className={`flex flex-col items-center ${isExpiring && !isFrozen ? "animate-pulse" : ""}`}
      >
        <div className={`text-4xl font-bold ${frozenTextColor}`}>
          {formatTime(timeLeft.seconds)}
        </div>
        <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {t("common.time.seconds")}
        </div>
      </div>
      {isFrozen && (
        <div className="ml-3 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-sm font-medium rounded-md border border-amber-200 dark:border-amber-800 animate-pulse">
          {t("assignments.timerPaused")}
        </div>
      )}
    </div>
  );
};

const PDFDownloadCard = ({
  assignment,
  showAssignStudent,
  setShowSuccessModal,
}: {
  assignment: BaseAssignmentResponseDto;
  showAssignStudent: boolean;
  setShowSuccessModal: (show: boolean) => void;
}) => {
  const isMobile = useMobile();
  const { t, i18n } = useTranslation();
  const [showStudentSelector, setShowStudentSelector] = useState(false);
  const [startQuizDuration, setStartQuizDuration] = useState<number>(
    assignment.duration ?? 120,
  );
  const [quizDuration, setQuizDuration] = useState<number>(10);
  const [
    shouldStudentsSeeAnswersAutomatically,
    setShouldStudentsSeeAnswersAutomatically,
  ] = useState<boolean>(
    assignment.shouldStudentsSeeAnswersAutomaticallyAfterFinish ?? false,
  );
  const [loadingButtons, setLoadingButtons] = useState<
    Array<"publish" | "extend" | "freeze" | "unfreeze" | "download">
  >([]);
  const [selectedStudents, setSelectedStudents] = useState<
    {
      classroomId: number;
      studentIds: number[];
    }[]
  >([]);
  const [newlyCreatedQuestions, setNewlyCreatedQuestions] = useState<
    EditAssignmentRequestBodyNewlyCreatedItem[]
  >([]);
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const getAssignmentFromTeacherQueryKey = getGetAssignmentFromTeacherQueryKey(
    Number(assignment.id),
  );

  const getAssignmentStudentsQueryKey = getGetAssignmentStudentsQueryKey(
    Number(assignment.id),
  );
  const selectorRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    setTimeout(() => {
      selectorRef.current!.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }, 200);
  };

  useEffect(() => {
    if (showStudentSelector && selectorRef.current) {
      scrollToBottom();
    }
  }, [showStudentSelector]);

  const handleCheckboxChange = async (checked: boolean) => {
    setShouldStudentsSeeAnswersAutomatically(checked);
    try {
      await updateAssignment({
        assignmentId: assignment.id,
        data: {
          shouldStudentsSeeAnswersAutomaticallyAfterFinish: checked,
        } as EditAssignmentRequestBody,
      });
    } catch (error) {
      console.error("Failed to update setting:", error);
      // Revert UI state on error
      setShouldStudentsSeeAnswersAutomatically(!checked);
    }
  };

  const handleShowAnswersToUsers = async () => {
    try {
      await updateAssignment({
        assignmentId: assignment.id,
        data: {
          shouldStudentsSeeAnswersAutomaticallyAfterFinish: true,
        } as EditAssignmentRequestBody,
      });
      setShouldStudentsSeeAnswersAutomatically(true);
      addToast({
        title: t("assignments.answersShownToStudents"),
        variant: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Failed to show answers to students:", error);
      addToast({
        title: t("assignments.showAnswersError"),
        variant: "error",
        duration: 3000,
      });
    }
  };

  const { mutateAsync: updateAssignment, isPending: isUpdating } =
    useEditAssignment({
      mutation: {
        onSuccess: () => {
          addToast({
            title: t("notifications.assignment.updateSuccess"),
            variant: "success",
            duration: 1000,
          });
          queryClient.invalidateQueries({
            queryKey: getAssignmentFromTeacherQueryKey,
          });
          setNewlyCreatedQuestions([]);
        },
        onError: () => {
          addToast({
            title: t("notifications.assignment.updateError"),
            description: t("notifications.assignment.updateErrorDescription"),
            variant: "error",
            duration: 3000,
          });
        },
      },
    });

  const handleDownload = async (showAnswers: boolean) => {
    try {
      const buttonType = "download";
      addLoadingButton(buttonType);
      const questions = JSON.parse(JSON.stringify(assignment.questions));
      if (!showAnswers) {
        questions.forEach((q: any) => {
          q.answer = "";
        });
      }

      const pdfData: PDFData = {
        showAnswers,
        questions,
        title: assignment.name,
        assignmentImageUrl: assignment.assignmentImageUrl,
      };

      const document = <PDFRenderer data={pdfData} />;
      const blob = await pdf(document).toBlob();
      const url = URL.createObjectURL(blob);

      // Create temporary link and trigger download
      const link = window.document.createElement("a");
      link.href = url;
      link.download = `${assignment.name}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);

      // Cleanup URL object
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("PDF generation error:", error);
      addToast({
        title: t("common.error"),
        description: t("common.errorDescription"),
        variant: "error",
      });
    } finally {
      removeLoadingButton("download");
    }
  };

  const handlePreview = async (showAnswers: boolean) => {
    try {
      const buttonType = "download";
      addLoadingButton(buttonType);
      const questions = JSON.parse(JSON.stringify(assignment.questions));
      if (!showAnswers) {
        questions.forEach((q: any) => {
          q.answer = "";
        });
      }

      const pdfData: PDFData = {
        showAnswers,
        questions,
        title: assignment.name,
        assignmentImageUrl: assignment.assignmentImageUrl,
      };

      const document = <PDFRenderer data={pdfData} />;
      const blob = await pdf(document).toBlob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (error) {
      console.error("PDF generation failed:", error);
    } finally {
      removeLoadingButton("download");
    }
  };

  const { mutateAsync: assignAssignment } = useAssignAssignmentToStudentsNew({
    mutation: {
      onSuccess: () => {
        setShowSuccessModal(true);

        queryClient.invalidateQueries({
          queryKey: getAssignmentFromTeacherQueryKey,
        });
        queryClient.invalidateQueries({
          queryKey: getAssignmentStudentsQueryKey,
        });
      },
      onError: () => {
        addToast({
          title: t("notifications.assignment.assignError"),
          description: t("notifications.assignment.assignErrorDescription"),
          variant: "error",
          duration: 3000,
        });
      },
    },
  });

  const handleAddQuestion = async (
    newQuestion:
      | FillTheBlanksQuestionCreateRequestDto
      | MatchQuestionCreateRequestDto
      | OpenEndedQuestionCreateRequestDto
      | TestQuestionCreateRequestDto
      | TrueFalseQuestionCreateRequestDto,
  ) => {
    const updatedQuestions = [...newlyCreatedQuestions, newQuestion];
    setNewlyCreatedQuestions(updatedQuestions);

    const editAssignmentData: EditAssignmentRequestBody = {
      newlyCreated: updatedQuestions,
    };

    try {
      await updateAssignment({
        assignmentId: assignment.id,
        data: editAssignmentData,
      });
    } catch (error) {
      console.error("Failed to add question:", error);
    }
  };

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value > 0) {
      setQuizDuration(value);
    }
  };

  const isAssignmentEditable =
    assignment.status === BaseAssignmentResponseDtoStatus.DRAFT &&
    showAssignStudent;

  const isButtonLoading = (
    buttonType: "publish" | "extend" | "freeze" | "unfreeze" | "download",
  ) => {
    return loadingButtons.includes(buttonType);
  };

  const addLoadingButton = (
    buttonType: "publish" | "extend" | "freeze" | "unfreeze" | "download",
  ) => {
    setLoadingButtons((prev) => [...prev, buttonType]);
  };

  const removeLoadingButton = (
    buttonType: "publish" | "extend" | "freeze" | "unfreeze" | "download",
  ) => {
    setLoadingButtons((prev) => prev.filter((btn) => btn !== buttonType));
  };

  return (
    <div className="mb-4">
      {assignment.status === BaseAssignmentResponseDtoStatus.IN_PROGRESS &&
        assignment.type === "QUIZ" && (
          <div className="mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg border border-blue-100 dark:border-blue-800 shadow-md">
            <div className="flex flex-col items-center">
              <h3 className="text-lg font-medium text-blue-800 dark:text-blue-200 mb-2">
                {assignment.lastAssignmentStopTime
                  ? t("assignments.timerStopped")
                  : t("assignments.timeRemaining")}
              </h3>
              <div className="flex items-center justify-center w-full">
                <QuizTimer
                  endDate={assignment.endDate}
                  isFrozen={!!assignment.lastAssignmentStopTime}
                />
              </div>

              <div className="mt-3 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="autoShowAnswers"
                  checked={shouldStudentsSeeAnswersAutomatically}
                  onChange={(e) => handleCheckboxChange(e.target.checked)}
                  className="checkbox checkbox-sm checkbox-primary"
                />
                <label
                  htmlFor="autoShowAnswers"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  {t("assignments.autoShowAnswers")}
                </label>
              </div>
            </div>
          </div>
        )}
      <div className="py-2 flex justify-between items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <DownloadButton
            icon={<Download size={16} />}
            text={t("assignments.pdf.withoutAnswers")}
            onClick={() => {
              if (isMobile) {
                handleDownload(false);
              } else {
                handlePreview(false);
              }
            }}
            loading={isButtonLoading("download")}
            variant="secondary"
            disabled={
              assignment.status === BaseAssignmentResponseDtoStatus.PENDING ||
              isButtonLoading("download")
            }
          />
          <DownloadButton
            icon={<Download size={16} />}
            text={t("assignments.pdf.withAnswers")}
            onClick={() => {
              if (isMobile) {
                handleDownload(true);
              } else {
                handlePreview(true);
              }
            }}
            loading={isButtonLoading("download")}
            variant="primary"
            disabled={
              assignment.status === BaseAssignmentResponseDtoStatus.PENDING ||
              isButtonLoading("download")
            }
          />
        </div>

        <div className="flex items-center gap-4">
          {isAssignmentEditable && (
            <QuestionAddModal
              onAddQuestion={handleAddQuestion}
              disabled={isUpdating}
              gradeLevel={assignment.grade}
            />
          )}

          {isAssignmentEditable && (
            <div className="flex gap-2">
              <Button
                size="md"
                rounded="lg"
                textColor="text-white"
                bgColor={
                  selectedStudents.length > 0
                    ? "bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600"
                    : "bg-primary-500 hover:bg-primary-600"
                }
                onClick={async () => {
                  if (showStudentSelector) {
                    await assignAssignment({
                      assignmentId: assignment.id,
                      data: { items: selectedStudents },
                    });
                    setSelectedStudents([]);
                    setShowStudentSelector(false);
                  } else {
                    setShowStudentSelector(true);
                  }
                }}
                disabled={selectedStudents.length === 0 && showStudentSelector}
                className={`flex items-center gap-2 group min-w-fit transition-all duration-300 ${
                  selectedStudents.length > 0
                    ? "shadow-md hover:shadow-lg scale-105 hover:scale-[1.07]"
                    : ""
                }`}
              >
                {!showStudentSelector && (
                  <svg
                    className="h-4 w-4 transition-transform duration-200 group-hover:scale-110"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                )}
                {selectedStudents.length > 0 && showStudentSelector && (
                  <svg
                    className="h-4 w-4 animate-pulse"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}

                {t("assignments.generator.steps.assignButton")}
              </Button>
            </div>
          )}
          {assignment.questions.some(
            (q) => q.type === BaseQuestionDtoType.OE,
          ) &&
            assignment.pendingReview &&
            ((assignment.type === "QUIZ" && assignment.status === "FINISHED") ||
              assignment.type === "HOMEWORK") && (
              <Button
                size="md"
                rounded="lg"
                textColor="text-white"
                bgColor="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
                onClick={async () => {
                  navigate(`/assignments/${assignment.id}/open-ended-review`);
                }}
                className="flex items-center gap-2 mr-2 group min-w-fit transition-all duration-300 shadow-md hover:shadow-lg scale-105 hover:scale-[1.07]"
              >
                <svg
                  className="h-4 w-4 animate-pulse"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                {t("assignments.generator.steps.startReview")}
              </Button>
            )}
          {assignment.status === BaseAssignmentResponseDtoStatus.ASSIGNED &&
            assignment.type === "QUIZ" && (
              <div className="flex gap-4 items-center">
                <div className="relative flex items-center gap-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 shadow-sm hover:shadow transition-all duration-200">
                  <Clock
                    size={18}
                    className="text-gray-500 dark:text-gray-400"
                  />
                  <input
                    type="number"
                    min="1"
                    value={startQuizDuration}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value) && value > 0) {
                        setStartQuizDuration(value);
                      }
                    }}
                    className="w-16 bg-transparent border-none focus:outline-none text-gray-700 dark:text-gray-300 text-sm font-medium"
                    aria-label={t("assignments.generator.steps.duration")}
                  />
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {i18n.language === "tr" ? "dk" : "min"}
                  </span>
                </div>
                <Button
                  size="md"
                  rounded="lg"
                  textColor="text-white"
                  bgColor="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600"
                  onClick={async () => {
                    try {
                      await startQuizAssignment(assignment.id, {
                        duration: startQuizDuration,
                      });
                      queryClient.invalidateQueries({
                        queryKey: getAssignmentFromTeacherQueryKey,
                      });
                      addToast({
                        title: t("assignments.generator.steps.startedQuiz"),
                        variant: "success",
                        duration: 4001,
                      });
                    } catch (error) {
                      console.error("Failed to start quiz:", error);
                    }
                  }}
                  className="flex items-center gap-2 group min-w-fit transition-all duration-300 shadow-md hover:shadow-lg scale-105 hover:scale-[1.07]"
                >
                  <svg
                    className="h-4 w-4 animate-pulse"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {t("assignments.generator.steps.startQuiz")}
                </Button>
              </div>
            )}
          {assignment.status === BaseAssignmentResponseDtoStatus.IN_PROGRESS &&
            assignment.type === "QUIZ" &&
            !assignment.lastAssignmentStopTime && (
              <div className="flex gap-4 items-center">
                <div className="relative flex items-center gap-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 shadow-sm hover:shadow transition-all duration-200">
                  <Clock
                    size={18}
                    className="text-gray-500 dark:text-gray-400"
                  />
                  <input
                    type="number"
                    min="1"
                    value={quizDuration}
                    onChange={handleDurationChange}
                    className="w-16 bg-transparent border-none focus:outline-none text-gray-700 dark:text-gray-300 text-sm font-medium"
                    aria-label={t("assignments.generator.steps.extendTime")}
                  />
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {i18n.language === "tr" ? "dk" : "min"}
                  </span>
                </div>
                <Button
                  size="md"
                  rounded="lg"
                  textColor="text-white"
                  bgColor="bg-blue-500 hover:bg-blue-600"
                  loading={isButtonLoading("extend")}
                  disabled={isButtonLoading("extend")}
                  onClick={async () => {
                    addLoadingButton("extend");
                    try {
                      await extendQuizAssignment(assignment.id, {
                        duration: quizDuration,
                      });
                      queryClient.invalidateQueries({
                        queryKey: getAssignmentFromTeacherQueryKey,
                      });
                      addToast({
                        title: t(
                          "assignments.generator.steps.extendedQuizTime",
                        ),
                        variant: "success",
                        duration: 3000,
                      });
                    } catch (error) {
                      console.error("Failed to extend quiz time:", error);
                    } finally {
                      removeLoadingButton("extend");
                    }
                  }}
                  className="flex items-center gap-2 min-w-fit transition-all duration-300"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  {t("assignments.generator.steps.extendTime")}
                </Button>
                <Button
                  size="md"
                  rounded="lg"
                  textColor="text-white"
                  bgColor="bg-amber-500 hover:bg-amber-600"
                  loading={isButtonLoading("freeze")}
                  disabled={isButtonLoading("freeze")}
                  onClick={async () => {
                    addLoadingButton("freeze");
                    try {
                      await freezeAssignment(assignment.id);
                      queryClient.invalidateQueries({
                        queryKey: getAssignmentFromTeacherQueryKey,
                      });
                      addToast({
                        title: t("assignments.generator.steps.quizStopped"),
                        variant: "success",
                        duration: 3000,
                      });
                    } catch (error) {
                      console.error("Failed to stop quiz:", error);
                    } finally {
                      removeLoadingButton("freeze");
                    }
                  }}
                  className="flex items-center gap-2 min-w-fit transition-all duration-300"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {t("assignments.generator.steps.stopQuiz")}
                </Button>
              </div>
            )}
          {assignment.status === BaseAssignmentResponseDtoStatus.IN_PROGRESS &&
            assignment.type === "QUIZ" &&
            assignment.lastAssignmentStopTime && (
              <div className="flex gap-4 items-center">
                <Button
                  size="md"
                  rounded="lg"
                  textColor="text-white"
                  bgColor="bg-green-500 hover:bg-green-600"
                  loading={isButtonLoading("unfreeze")}
                  disabled={isButtonLoading("unfreeze")}
                  onClick={async () => {
                    addLoadingButton("unfreeze");
                    try {
                      await unfreezeAssignment(assignment.id);
                      queryClient.invalidateQueries({
                        queryKey: getAssignmentFromTeacherQueryKey,
                      });
                      addToast({
                        title: t("assignments.generator.steps.restartedQuiz"),
                        variant: "success",
                        duration: 3000,
                      });
                    } catch (error) {
                      console.error("Failed to restart quiz:", error);
                    } finally {
                      removeLoadingButton("unfreeze");
                    }
                  }}
                  className="flex items-center gap-2 group min-w-fit transition-all duration-300 shadow-md hover:shadow-lg scale-105 hover:scale-[1.07]"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {t("assignments.generator.steps.restartQuiz")}
                </Button>
              </div>
            )}
          {assignment.status === BaseAssignmentResponseDtoStatus.FINISHED &&
            assignment.type === "QUIZ" &&
            !shouldStudentsSeeAnswersAutomatically && (
              <div className="flex gap-4 items-center">
                <Button
                  size="md"
                  rounded="lg"
                  textColor="text-white"
                  bgColor="bg-purple-500 hover:bg-purple-600"
                  onClick={handleShowAnswersToUsers}
                  className="flex items-center gap-2 group min-w-fit transition-all duration-300 shadow-md hover:shadow-lg scale-105 hover:scale-[1.07]"
                >
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  {t("assignments.showAnswersToStudents")}
                </Button>
              </div>
            )}
        </div>
      </div>
      {showStudentSelector && isAssignmentEditable && (
        <>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 animate-fadeIn mt-2">
            <ClassroomAndStudentSelector
              selectedStudents={selectedStudents}
              setSelectedStudents={setSelectedStudents}
              shouldUseModal={false}
              scrollToBottom={scrollToBottom}
            />
          </div>
          <div ref={selectorRef} className="h-10"></div>
        </>
      )}
    </div>
  );
};

export default PDFDownloadCard;
